import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLocale } from '@/contexts/LocaleContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import TexHubLogo from '@/components/brand/TexHubLogo';
import RegionSelector from '@/components/RegionSelector';
import { Mail, Loader2, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const ForgotPassword = () => {
  const { isUSA } = useLocale();
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [email, setEmail] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      if (response.ok) {
        setSent(true);
      } else {
        const data = await response.json();
        toast.error(data.detail || (isUSA ? 'Error sending email' : 'Error al enviar correo'));
      }
    } catch (error) {
      // Even on error, show success to prevent email enumeration
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="container-app">
          <div className="flex items-center justify-between h-16">
            <Link to="/">
              <TexHubLogo size="default" />
            </Link>
            <div className="flex items-center gap-3">
              <RegionSelector variant="minimal" showLabel={false} />
              <Link to="/login">
                <Button variant="outline" size="sm" style={{ borderColor: '#1E4C8E', color: '#1E4C8E' }}>
                  {isUSA ? 'Log in' : 'Iniciar sesión'}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-slate-900" style={{fontFamily: 'Manrope, sans-serif'}}>
              {isUSA ? 'Reset your password' : 'Recuperar contraseña'}
            </h1>
            <p className="text-slate-600 mt-2">
              {isUSA 
                ? "Enter your email and we'll send you instructions" 
                : 'Ingresa tu correo y te enviaremos instrucciones'}
            </p>
          </div>

          <Card className="bg-white border-slate-200">
            <CardContent className="pt-6">
              {sent ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    {isUSA ? 'Check your email' : 'Revisa tu correo'}
                  </h3>
                  <p className="text-slate-600 text-sm mb-6">
                    {isUSA 
                      ? `If an account exists with ${email}, you will receive password reset instructions.`
                      : `Si existe una cuenta con ${email}, recibirás instrucciones para restablecer tu contraseña.`}
                  </p>
                  <Link to="/login">
                    <Button variant="outline" className="w-full">
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      {isUSA ? 'Back to login' : 'Volver al inicio de sesión'}
                    </Button>
                  </Link>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">{isUSA ? 'Email address' : 'Correo electrónico'}</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        id="email"
                        type="email"
                        placeholder={isUSA ? 'you@company.com' : 'tu@empresa.com'}
                        className="h-12 pl-10"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        data-testid="forgot-email-input"
                      />
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full h-12 text-base font-semibold hover:opacity-90"
                    style={{ backgroundColor: '#1E4C8E' }}
                    disabled={loading}
                    data-testid="forgot-submit-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        {isUSA ? 'Sending...' : 'Enviando...'}
                      </>
                    ) : (
                      isUSA ? 'Send reset instructions' : 'Enviar instrucciones'
                    )}
                  </Button>

                  <Link to="/login" className="block">
                    <Button variant="ghost" className="w-full">
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      {isUSA ? 'Back to login' : 'Volver al inicio de sesión'}
                    </Button>
                  </Link>
                </form>
              )}
            </CardContent>
          </Card>

          <p className="text-center text-sm text-slate-500 mt-6">
            {isUSA 
              ? "Remember your password? " 
              : '¿Recuerdas tu contraseña? '}
            <Link to="/login" className="font-medium hover:underline" style={{ color: '#1E4C8E' }}>
              {isUSA ? 'Log in' : 'Inicia sesión'}
            </Link>
          </p>
        </div>
      </main>
    </div>
  );
};

export default ForgotPassword;
